import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import {
  IFiltroLegislacao,
  ILegisnormas,
  ISubfiltros,
  InformacoesGeraisLegislacao,
  PageLegislacao,
} from '../types';
import LegislacaoService from '@/views/legislacao/service/LegislacaoService';
import OrigemNormaLegService from '@/views/legislacao/service/OrigemNormaLegService';

import SdsSnackbarQueue from '@ungp/sds-snackbar-queue';
import { DataOptions } from 'vuetify';
import { isEqual } from 'lodash';
import {
  transformarDadosSubFilter,
  verificarSePossuiItensSelecionadosOuExpandidos,
} from '@/utils/transform';
import { cloneDeep } from '@apollo/client/utilities';
import moment from 'moment';

export const ConsultaLegislacaoStore = defineStore(
  'consultaLegislacaoStore',
  () => {
    const pageLegislacao = ref<PageLegislacao>({});

    const pageSubfiltros = ref<ISubfiltros[]>([]);

    const pageSubfiltrosOld = ref<ISubfiltros[] | undefined>(undefined);

    const dataOptions = ref({
      page: 1,
      itemsPerPage: 10,
      sortBy: [''],
      sortDesc: [true],
    } as DataOptions);

    const filtroLegislacao = ref<IFiltroLegislacao>({
      expandirPesquisaAvancada: false,
      legislativasNormativas: [
        {
          tipoNorma: null,
          nuNorma: '',
          deArtigo: '',
          deParagrafo: '',
          deInciso: '',
          deAlinea: '',
          deNumero: '',
        },
      ],
      flEmenta: true,
      flInteiroTeor: true,
      ordenacao: 'dtlegislacao desc',
    });

    const informacoesGeraisLegislacao = ref({} as InformacoesGeraisLegislacao);

    const showFilter = ref(false);

    const legisnormas = computed(() => pageLegislacao.value.legisnormas || []);

    const legisnormasSelecionadas = ref<ILegisnormas[]>([]);

    const codigosLegisnormasSelecionadas = computed<number[] | []>(() =>
      legisnormasSelecionadas.value.map((legisnorma) => legisnorma.cdLegislacao)
    );

    const subFiltrosCategorias = computed(() => {
      if (pageSubfiltros.value) {
        const resultadoFinal = transformarDadosSubFilter(pageSubfiltros.value);

        return resultadoFinal;
      }
      return {
        classes: [],
        relatores: [],
        comarcas: [],
        orgaosJulgadores: [],
        revistas: [],
        anos: [],
        prolatores: [],
      };
    });

    const totalHits = computed(() => pageLegislacao.value.totalHits || 0);

    const limiteTotalHists = 10000;

    const paginacao = computed(() => {
      const { page } = dataOptions.value;
      return page - 1;
    });

    const totalElementos = computed(() => {
      if (totalHits.value > limiteTotalHists) return limiteTotalHists;
      return totalHits.value;
    });

    const params = computed<IFiltroLegislacao>(() => {
      const filtro = filtroLegislacao.value;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const subFilter: any = {};
      pageSubfiltros.value.forEach((subFiltro) => {
        subFilter[subFiltro.campo] = subFiltro.valoresSubFiltroResponseDTO
          .filter((valores) => valores.checked)
          .map((valores) => valores.descricao);
      });

      return {
        dePesquisaTermos: filtro.dePesquisaTermos ?? '',
        flPesquisaPlurais: 'N',
        flMunicipal: filtro.flMunicipal || undefined,
        flEstadual: filtro.flEstadual || undefined,
        flFederal: filtro.flFederal || undefined,
        flEmenta: filtro.flEmenta,
        flInteiroTeor: filtro.flInteiroTeor,
        legislativasNormativas: filtro.legislativasNormativas,
        subFilter: subFilter ?? {
          materias: [],
          orgaosExpedidores: [],
          anos: [],
          tiposNorma: [],
        },
        numPag: paginacao.value,
        ordenacao: filtro.ordenacao,
        tipoNormaSelected: filtro.tipoNormaSelected,
        orgaoExpedidorSelected: filtro.orgaoExpedidorSelected,
        fonteSelected: filtro.fonteSelected,
        subFiltrosOld: filtro.subFiltrosOld,
        dtPublicacaoInicio: filtro.dtPublicacaoInicio
          ? moment(filtro.dtPublicacaoInicio).valueOf()
          : undefined,
        dtPublicacaoFim: filtro.dtPublicacaoFim
          ? moment(filtro.dtPublicacaoFim).valueOf()
          : undefined,
        dtNorma: filtro.dtNorma ? moment(filtro.dtNorma).valueOf() : undefined,
        nuNorma: filtro.nuNorma || undefined,
      };
    });

    const baixarOrigemNorma = (
      hashCdFonte: string,
      hashCdOrigemNorma: string
    ) => {
      OrigemNormaLegService.baixarOrigemNorma(hashCdFonte, hashCdOrigemNorma);
    };

    const adicionarItemLegislativasNormativas = () => {
      filtroLegislacao.value.legislativasNormativas.push({
        tipoNorma: null,
        nuNorma: '',
        deArtigo: '',
        deParagrafo: '',
        deInciso: '',
        deAlinea: '',
        deNumero: '',
      });
    };

    const removerItemLegislativasNormativas = (index: number) => {
      filtroLegislacao.value.legislativasNormativas.splice(index, 1);
    };

    const consultaLegislacao = async (cdLegislacao: string) => {
      const messageError = 'Não foi possível buscar os registros';
      try {
        const response = await LegislacaoService.consultarLegislacao(
          cdLegislacao
        );

        if (response.hasError) {
          SdsSnackbarQueue.error(response.error?.message || messageError);
          return;
        }
        informacoesGeraisLegislacao.value = response.content;
      } catch (error) {
        SdsSnackbarQueue.error(messageError);
      }
    };

    const consultaListaLegislacao = async (initialPagination = false) => {
      const messageError = 'Não foi possível buscar os registros';
      try {
        pageLegislacao.value = {};
        if (initialPagination) {
          dataOptions.value.page = 1;
          pageSubfiltros.value = [];
          pageSubfiltrosOld.value = undefined;
          showFilter.value = false;
          legisnormasSelecionadas.value = [];
        }

        const response = await LegislacaoService.consultarListaLegislacao(
          params.value
        );

        if (response.hasError) {
          SdsSnackbarQueue.error(response.error?.message || messageError);
          return;
        }

        pageSubfiltrosOld.value = cloneDeep(pageSubfiltros.value);

        pageLegislacao.value = response.content!;

        const subfiltrosAtualizados =
          pageLegislacao.value.subfiltros || undefined;

        if (pageSubfiltrosOld.value && pageSubfiltrosOld.value.length > 0) {
          pageSubfiltros.value = verificarSePossuiItensSelecionadosOuExpandidos(
            subfiltrosAtualizados,
            pageSubfiltrosOld.value
          );
        } else {
          pageSubfiltros.value = subfiltrosAtualizados!;
        }
      } catch (error) {
        SdsSnackbarQueue.error(messageError);
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const atualizarPaginacao = (refElemento: any, pagina: number) => {
      if (!isEqual(dataOptions, pagina)) {
        dataOptions.value.page = pagina;
        consultaListaLegislacao();
        refElemento.scrollIntoView({ behavior: 'smooth' });
      }
    };

    const atualizarOrdenacao = (ordenacao: { value: string }) => {
      filtroLegislacao.value.ordenacao = ordenacao.value;
      consultaListaLegislacao();
    };

    const limparTodosFiltroLegislacao = () => {
      pageLegislacao.value = {};

      pageSubfiltros.value = [];

      pageSubfiltrosOld.value = undefined;

      limparDataOptions();

      filtroLegislacao.value = {
        expandirPesquisaAvancada: false,
        legislativasNormativas: [
          {
            tipoNorma: null,
            nuNorma: '',
            deArtigo: '',
            deParagrafo: '',
            deInciso: '',
            deAlinea: '',
            deNumero: '',
          },
        ],
        flEmenta: true,
        flInteiroTeor: true,
        ordenacao: 'dtlegislacao desc',
      };
    };

    const limparDataOptions = () => {
      dataOptions.value = {
        page: 1,
        itemsPerPage: 10,
        sortBy: [''],
        sortDesc: [true],
      } as DataOptions;
    };

    const infoGeraisDownload = (formato: 'A' | 'S' | 'D') => {
      const cdLegislacao = informacoesGeraisLegislacao.value.cdLegislacao;
      download(cdLegislacao, formato);
    };

    const downloadSelecionados = (formato: 'A' | 'S' | 'D') => {
      download(codigosLegisnormasSelecionadas.value, formato);
    };

    const download = (
      cdLegislacao: number | number[],
      formato: 'A' | 'S' | 'D'
    ) => {
      const stringCdLegislacao = Array.isArray(cdLegislacao)
        ? cdLegislacao.join(',')
        : cdLegislacao;
      const url = `/mat-report/publico/gcn/api/v1.0/legislacaoNorma?legislacoes=${stringCdLegislacao}&normas=&formato=${formato}`;
      window.open(url, '_blank');
    };

    return {
      pageLegislacao,
      pageSubfiltros,
      pageSubfiltrosOld,
      dataOptions,
      paginacao,
      totalHits,
      legisnormas,
      totalElementos,
      filtroLegislacao,
      informacoesGeraisLegislacao,
      subFiltrosCategorias,
      showFilter,
      legisnormasSelecionadas,
      consultaLegislacao,
      downloadSelecionados,
      consultaListaLegislacao,
      adicionarItemLegislativasNormativas,
      baixarOrigemNorma,
      removerItemLegislativasNormativas,
      limparTodosFiltroLegislacao,
      limparDataOptions,
      atualizarPaginacao,
      atualizarOrdenacao,
      download,
      infoGeraisDownload,
    };
  }
);
